// src/components/Pages/ForParents.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
const faqs: { q: string; a: string }[] = [
    {
        q: 'Do I need to create an account for my child?',
        a: 'Yes — and that is intentional. We require a parent email to create any child account, so you are always in the loop. You set up the account, choose the settings, and your child uses it under your supervision.',
    },
    {
        q: 'Is Scriblish free?',
        a: 'Yes, the core platform is completely free with no ads. Your child can write, publish, and read stories without paying anything.',
    },
    {
        q: 'Can I see what my child is writing?',
        a: 'Absolutely. Every story your child writes is visible from your parent dashboard. You can see drafts, published stories, and any feedback they have received.',
    },
    {
        q: 'What if my child sees something inappropriate?',
        a: 'All stories are reviewed by our moderation team before they go live. If something slips through, your child can tap the Report button and our team reviews it within 24 hours. You can also email us directly at scriblishofficial@gmail.com.',
    },
    {
        q: 'Can I delete my child\'s account?',
        a: 'Yes, at any time. Go to Settings, then Manage Account, then Delete Account. You can download all their stories first. All personal data is permanently removed within 30 days.',
    },
    {
        q: 'What personal information does Scriblish collect?',
        a: 'We collect your email (as the parent), your child\'s username and avatar, and usage data like stories written and badges earned. We never collect real names, addresses, phone numbers, or school information. We are fully COPPA compliant.',
    },
    {
        q: 'Can other kids contact my child?',
        a: 'No. There is no direct messaging on Scriblish. Kids interact only through reactions on published stories. There is no way for another user to privately contact your child.',
    },
    {
        q: 'What age is Scriblish for?',
        a: 'Scriblish is designed for children ages 10 to 13. The writing tools, prompts, and community are all tailored for this age group.',
    },
];
 
const benefits = [
    { icon: '✍️', title: 'Builds real writing skills',  desc: 'Story planning, chapter structure, descriptive writing — all taught through doing, not drilling.' },
    { icon: '📖', title: 'Grows a love of reading',     desc: 'Kids read other young authors stories, which is far more motivating than a textbook passage.' },
    { icon: '🎨', title: 'Sparks creativity',           desc: 'From cover design to character building, every part of the platform encourages creative thinking.' },
    { icon: '🏆', title: 'Builds confidence',           desc: 'Publishing a story and getting positive reactions from real readers is enormously motivating for kids.' },
    { icon: '🛡️', title: 'Completely safe',            desc: 'No ads, no direct messaging, no personal data shared. Content is moderated before it goes live.' },
    { icon: '🆓', title: 'Free to use',                desc: 'The full writing experience is free. No hidden costs, no in-app purchases for kids.' },
];
 
const howItWorks = [
    { step: '1', title: 'You create the account',     desc: 'Sign up with your email. Your child picks a fun username — no real names allowed.' },
    { step: '2', title: 'Your child starts writing',  desc: 'They can start a story right away using guided tools: Story Planner, Character Builder, and a chapter editor.' },
    { step: '3', title: 'They design a cover',        desc: 'Using the Cover Designer, they pick backgrounds, add stickers, and title their book.' },
    { step: '4', title: 'They publish (optionally)',  desc: 'When they are ready, they can share their story with the Scriblish community. All stories are reviewed first.' },
    { step: '5', title: 'You follow along',           desc: 'From your dashboard, you can read every story, see their progress, and cheer them on.' },
];
 
const safetyPoints = [
    { icon: '🔒', title: 'No real names, ever',   desc: 'Children use only a fun username. Real names, addresses, phone numbers, and school names are blocked.' },
    { icon: '🤖', title: 'Content is moderated',  desc: 'Every story is reviewed by our team before it is published. AI filters catch issues first.' },
    { icon: '💬', title: 'No direct messaging',   desc: 'Kids can only interact through reactions on published stories. There is no inbox.' },
    { icon: '📋', title: 'COPPA compliant',        desc: 'We follow all Children\'s Online Privacy Protection Act requirements. We never sell your child\'s data.' },
    { icon: '🗑️', title: 'Delete anytime',        desc: 'You can delete your child\'s account and all their data at any time, no questions asked.' },
];
 
const monitorItems = [
    'Read every story they have written — drafts and published',
    'See their writing streak and badge progress',
    'Review any stories before they are published publicly',
    'Set their account to private writing mode',
    'Change sharing settings at any time',
    'Download all their stories as a keepsake',
];
 
const ForParents: React.FC = () => {
    const navigate = useNavigate();
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
 
    const primaryBtnStyle: React.CSSProperties = {
        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
        color: '#fff', border: 'none', borderRadius: '9999px',
        padding: '13px 32px', fontSize: '0.95rem', fontWeight: '700',
        cursor: 'pointer', fontFamily: "'Epilogue', sans-serif",
        boxShadow: `0 4px 14px ${C.primary}40`,
        transition: 'opacity 0.15s, transform 0.15s',
    };
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
 
            {/* Hero */}
            <div style={{ background: `linear-gradient(135deg, ${C.primaryContainer} 0%, ${C.surfaceLow} 100%)`, padding: '64px 24px 80px' }}>
                <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
                    <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>👨‍👩‍👧‍👦</div>
                    <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '2.6rem', fontWeight: '900', color: C.onBackground, margin: '0 0 16px 0' }}>
                        Scriblish for Parents
                    </h1>
                    <p style={{ fontSize: '1.05rem', color: C.onSurfaceVariant, margin: '0 0 32px 0', lineHeight: '1.7' }}>
                        Everything you need to know about keeping your child safe, engaged, and writing their best stories.
                    </p>
                    <button
                        onClick={() => navigate('/signup')}
                        style={primaryBtnStyle}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        Create a Free Account
                    </button>
                </div>
            </div>
 
            <div style={{ maxWidth: '800px', margin: '0 auto', padding: '64px 24px 80px' }}>
 
                {/* Benefits */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Why kids thrive on Scriblish
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 28px 0', lineHeight: '1.6' }}>
                        Writing is one of the most important skills your child will ever develop. Scriblish makes it feel like play.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
                        {benefits.map((b, i) => (
                            <div key={i} style={{ background: '#fff', borderRadius: '16px', padding: '22px', border: `1px solid ${C.surfaceHigh}`, display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>{b.icon}</div>
                                <div>
                                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.9rem', color: C.onBackground, marginBottom: '4px' }}>{b.title}</div>
                                    <div style={{ fontSize: '0.83rem', color: C.onSurfaceVariant, lineHeight: '1.55' }}>{b.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
 
                {/* How it works */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        How it works
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 24px 0', lineHeight: '1.6' }}>
                        Getting your child started takes about two minutes.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {howItWorks.map(item => (
                            <div key={item.step} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', background: '#fff', borderRadius: '14px', padding: '18px 22px', border: `1px solid ${C.surfaceHigh}` }}>
                                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: C.primaryContainer, color: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '0.95rem', flexShrink: 0 }}>
                                    {item.step}
                                </div>
                                <div>
                                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.95rem', color: C.onBackground, marginBottom: '2px' }}>{item.title}</div>
                                    <div style={{ fontSize: '0.87rem', color: C.onSurfaceVariant, lineHeight: '1.55' }}>{item.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
 
                {/* Safety */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Safety and privacy
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 20px 0', lineHeight: '1.6' }}>
                        We built Scriblish with parents in mind. Every decision puts your child first.
                    </p>
                    <div style={{ background: C.primaryContainer, borderRadius: '20px', padding: '28px 32px', marginBottom: '14px' }}>
                        {safetyPoints.map((item, i) => (
                            <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: i < safetyPoints.length - 1 ? '18px' : 0 }}>
                                <div style={{ fontSize: '1.3rem', flexShrink: 0, marginTop: '2px' }}>{item.icon}</div>
                                <div>
                                    <div style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '0.9rem', color: C.primary, marginBottom: '2px' }}>{item.title}</div>
                                    <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, lineHeight: '1.55' }}>{item.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <a href="/safety" style={{ color: C.primary, fontWeight: '700', fontSize: '0.9rem', fontFamily: "'Epilogue', sans-serif", textDecoration: 'none' }}>
                            Read the full Safety Center →
                        </a>
                    </div>
                </section>
 
                {/* Monitor */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                        Staying involved
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 20px 0', lineHeight: '1.6' }}>
                        From your parent dashboard you can see everything your child is up to.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                        {monitorItems.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#fff', borderRadius: '12px', padding: '14px 16px', border: `1px solid ${C.surfaceHigh}` }}>
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: C.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>✓</div>
                                <div style={{ fontSize: '0.87rem', color: C.onSurface, lineHeight: '1.5' }}>{item}</div>
                            </div>
                        ))}
                    </div>
                </section>
 
                {/* FAQ */}
                <section style={{ marginBottom: '64px' }}>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.8rem', fontWeight: '900', color: C.onBackground, margin: '0 0 24px 0' }}>
                        Frequently asked questions
                    </h2>
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '8px 32px', border: `1px solid ${C.surfaceHigh}` }}>
                        {faqs.map((faq, i) => (
                            <div key={i} style={{ borderBottom: i < faqs.length - 1 ? `1px solid ${C.surfaceHigh}` : 'none' }}>
                                <button
                                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '18px 0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}
                                >
                                    <span style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '0.95rem', fontWeight: '800', color: C.onBackground, lineHeight: '1.4' }}>{faq.q}</span>
                                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: expandedFaq === i ? C.primary : C.surfaceLow, color: expandedFaq === i ? '#fff' : C.onSurfaceVariant, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: '700', flexShrink: 0, transition: 'all 0.15s' }}>
                                        {expandedFaq === i ? '−' : '+'}
                                    </div>
                                </button>
                                {expandedFaq === i && (
                                    <div style={{ paddingBottom: '18px', fontSize: '0.9rem', color: C.onSurfaceVariant, lineHeight: '1.7' }}>
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
 
                {/* CTA */}
                <div style={{ background: C.primaryContainer, borderRadius: '20px', padding: '40px', textAlign: 'center', marginBottom: '48px' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>✨</div>
                    <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onBackground, margin: '0 0 10px 0' }}>
                        Ready to get started?
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: C.onSurfaceVariant, margin: '0 0 24px 0', lineHeight: '1.6' }}>
                        Create a free account and your child can start writing their first story today.
                    </p>
                    <button
                        onClick={() => navigate('/signup')}
                        style={primaryBtnStyle}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                        Create a Free Account
                    </button>
                    <div style={{ marginTop: '14px', fontSize: '0.85rem', color: C.onSurfaceVariant }}>
                        Questions? Email{' '}
                        <a href="mailto:scriblishofficial@gmail.com" style={{ color: C.primary, fontWeight: '700', textDecoration: 'none' }}>
                            scriblishofficial@gmail.com
                        </a>
                    </div>
                </div>
 
                {/* Footer links */}
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {[
                        { label: 'Safety Center',        href: '/safety' },
                        { label: 'Privacy Policy',       href: '/privacy-policy' },
                        { label: 'Community Guidelines', href: '/community-guidelines' },
                        { label: 'For Teachers',         href: '/for-teachers' },
                    ].map(({ label, href }) => (
                        <a
                            key={label}
                            href={href}
                            style={{ color: C.primary, textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', fontFamily: "'Epilogue', sans-serif", transition: 'opacity 0.15s' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                        >
                            {label}
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
};
 
export default ForParents;